import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Tabs, Tree, Input, Spin, Avatar, Button, Tag } from 'antd';
import {
  UserOutlined,
  ApartmentOutlined,
  SearchOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import {
  PermissionApi,
  type GranteeVO,
  type DeptTreeNode,
  type DeptUserTreeNode,
  type UserPickItem,
} from '../../api/permissionApi';

interface GranteePickerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selected: GranteeVO[]) => void;
  initialSelected: GranteeVO[];
}

type UserMap = Map<number, string>;
type DeptMap = Map<number, string>;

// ——— 树数据构建 ———

const buildUserTreeData = (nodes: DeptUserTreeNode[]): DataNode[] =>
  nodes.map(dept => ({
    key: `dept-${dept.id}`,
    title: dept.deptName,
    icon: <ApartmentOutlined />,
    checkable: false,
    selectable: false,
    children: [
      ...(dept.users ?? []).map((u: UserPickItem) => ({
        key: `user-${u.userId}`,
        title: u.username,
        icon: <UserOutlined />,
        isLeaf: true,
      })),
      ...buildUserTreeData(dept.children ?? []),
    ],
  }));

const buildDeptTreeData = (nodes: DeptTreeNode[]): DataNode[] =>
  nodes.map(dept => {
    const children = buildDeptTreeData(dept.children ?? []);
    return {
      key: `dept-${dept.id}`,
      title: dept.deptName,
      icon: <ApartmentOutlined />,
      ...(children.length ? { children } : { isLeaf: true }),
    };
  });

// ——— 工具函数 ———

const collectUserMap = (nodes: DeptUserTreeNode[], map: UserMap) => {
  nodes.forEach(n => {
    (n.users ?? []).forEach(u => map.set(u.userId, u.username));
    collectUserMap(n.children ?? [], map);
  });
};

const collectDeptMap = (nodes: DeptTreeNode[], map: DeptMap) => {
  nodes.forEach(n => {
    map.set(n.id, n.deptName);
    collectDeptMap(n.children ?? [], map);
  });
};

/** 判断 deptId 是否有祖先在 checkedSet 中（用于过滤级联子部门） */
const hasAncestorSelected = (
  nodes: DeptTreeNode[],
  targetId: number,
  checkedSet: Set<number>,
  ancestors: number[] = []
): boolean => {
  for (const n of nodes) {
    if (n.id === targetId) return ancestors.some(a => checkedSet.has(a));
    if (hasAncestorSelected(n.children ?? [], targetId, checkedSet, [...ancestors, n.id])) return true;
  }
  return false;
};

/** 从勾选的 dept key 列表中只保留最顶层（去掉被祖先覆盖的子部门） */
const filterTopLevel = (keys: React.Key[], rawDeptNodes: DeptTreeNode[]): number[] => {
  const deptIds = keys
    .map(String)
    .filter(k => k.startsWith('dept-'))
    .map(k => Number(k.replace('dept-', '')));
  const checkedSet = new Set(deptIds);
  return deptIds.filter(id => !hasAncestorSelected(rawDeptNodes, id, checkedSet));
};

// ——— 主组件 ———

const GranteePickerModal: React.FC<GranteePickerModalProps> = ({
  open,
  onClose,
  onConfirm,
  initialSelected,
}) => {
  const [activeTab, setActiveTab] = useState<'user' | 'dept'>('user');

  const [userKeyword, setUserKeyword] = useState('');
  const [deptKeyword, setDeptKeyword] = useState('');
  const [userTreeData, setUserTreeData] = useState<DataNode[]>([]);
  const [deptTreeData, setDeptTreeData] = useState<DataNode[]>([]);
  const [rawDeptNodes, setRawDeptNodes] = useState<DeptTreeNode[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [deptLoading, setDeptLoading] = useState(false);

  const [userMap, setUserMap] = useState<UserMap>(new Map());
  const [deptMap, setDeptMap] = useState<DeptMap>(new Map());

  // 唯一数据源：右侧已选列表
  const [selected, setSelected] = useState<GranteeVO[]>([]);

  // 树的受控勾选 key（含级联子节点，用于驱动树的视觉状态）
  const [checkedUserKeys, setCheckedUserKeys] = useState<React.Key[]>([]);
  const [checkedDeptKeys, setCheckedDeptKeys] = useState<React.Key[]>([]);

  // 打开时初始化
  useEffect(() => {
    if (!open) return;
    setSelected([...initialSelected]);
    // 从 initialSelected 反推树的勾选 key
    setCheckedUserKeys(
      initialSelected.filter(g => g.granteeType === 'USER').map(g => `user-${g.granteeId}`)
    );
    setCheckedDeptKeys(
      initialSelected.filter(g => g.granteeType === 'DEPARTMENT').map(g => `dept-${g.granteeId}`)
    );
    setUserKeyword('');
    setDeptKeyword('');
    loadUserTree('');
    loadDeptTree('');
  }, [open]);

  const loadUserTree = useCallback(async (keyword: string) => {
    setUserLoading(true);
    try {
      const data = await PermissionApi.getUserPickTree(keyword || undefined);
      setUserTreeData(buildUserTreeData(data));
      const map: UserMap = new Map();
      collectUserMap(data, map);
      setUserMap(map);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const loadDeptTree = useCallback(async (keyword: string) => {
    setDeptLoading(true);
    try {
      const data = await PermissionApi.getDeptTree(keyword || undefined);
      setRawDeptNodes(data);
      setDeptTreeData(buildDeptTreeData(data));
      const map: DeptMap = new Map();
      collectDeptMap(data, map);
      setDeptMap(map);
    } finally {
      setDeptLoading(false);
    }
  }, []);

  // 人员树勾选：delta 更新 selected
  const handleUserCheck = (newKeys: React.Key[]) => {
    const prevSet = new Set(checkedUserKeys.map(String));
    const newSet = new Set(newKeys.map(String));

    const added = newKeys.filter(k => String(k).startsWith('user-') && !prevSet.has(String(k)));
    const removed = checkedUserKeys.filter(k => String(k).startsWith('user-') && !newSet.has(String(k)));

    setCheckedUserKeys(newKeys);
    setSelected(prev => {
      let next = [...prev];
      added.forEach(key => {
        const userId = Number(String(key).replace('user-', ''));
        if (!next.some(g => g.granteeType === 'USER' && g.granteeId === userId)) {
          next.push({
            granteeType: 'USER',
            granteeId: userId,
            displayName: userMap.get(userId) ?? String(userId),
            avatarUrl: null,
          });
        }
      });
      removed.forEach(key => {
        const userId = Number(String(key).replace('user-', ''));
        next = next.filter(g => !(g.granteeType === 'USER' && g.granteeId === userId));
      });
      return next;
    });
  };

  // 部门树勾选：基于完整 checked keys 计算顶层部门集合，保证父部门覆盖子部门时子部门被移除
  const handleDeptCheck = (newKeys: React.Key[]) => {
    setCheckedDeptKeys(newKeys);

    // 基于当前全部勾选 key 计算顶层部门（祖先已被勾选的子部门会被过滤掉）
    const topLevelIds = filterTopLevel(newKeys, rawDeptNodes);
    const topSet = new Set(topLevelIds);

    setSelected(prev => {
      // 保留非部门项，以及仍为顶层的部门（保持原顺序）
      const kept = prev.filter(
        g => g.granteeType !== 'DEPARTMENT' || topSet.has(g.granteeId)
      );
      const existingDeptIds = new Set(
        kept.filter(g => g.granteeType === 'DEPARTMENT').map(g => g.granteeId)
      );
      const toAdd: GranteeVO[] = topLevelIds
        .filter(id => !existingDeptIds.has(id))
        .map(id => ({
          granteeType: 'DEPARTMENT',
          granteeId: id,
          displayName: deptMap.get(id) ?? String(id),
          avatarUrl: null,
        }));
      return [...kept, ...toAdd];
    });
  };

  // 右侧 X 删除：同步取消树勾选
  const handleRemove = (granteeType: GranteeVO['granteeType'], granteeId: number) => {
    setSelected(prev => prev.filter(g => !(g.granteeType === granteeType && g.granteeId === granteeId)));
    if (granteeType === 'USER') {
      setCheckedUserKeys(prev => prev.filter(k => String(k) !== `user-${granteeId}`));
    } else {
      setCheckedDeptKeys(prev => prev.filter(k => String(k) !== `dept-${granteeId}`));
    }
  };

  const handleConfirm = () => onConfirm(selected);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="选择人员/部门"
      width={720}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={handleConfirm}>确认</Button>
        </div>
      }
    >
      <div className="flex gap-3" style={{ minHeight: 400 }}>
        {/* 左栏：选择区 */}
        <div className="flex-1 border rounded p-2 overflow-hidden" style={{ minWidth: 0 }}>
          <Tabs
            activeKey={activeTab}
            onChange={key => setActiveTab(key as 'user' | 'dept')}
            size="small"
            items={[
              {
                key: 'user',
                label: '人员',
                children: (
                  <>
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="搜索用户名"
                      value={userKeyword}
                      onChange={e => { setUserKeyword(e.target.value); loadUserTree(e.target.value); }}
                      className="mb-2"
                      allowClear
                    />
                    {userLoading ? (
                      <div className="flex justify-center mt-8"><Spin /></div>
                    ) : (
                      <Tree
                        checkable
                        showIcon
                        treeData={userTreeData}
                        checkedKeys={checkedUserKeys}
                        onCheck={keys => handleUserCheck(keys as React.Key[])}
                        style={{ maxHeight: 300, overflowY: 'auto' }}
                      />
                    )}
                  </>
                ),
              },
              {
                key: 'dept',
                label: '部门',
                children: (
                  <>
                    <Input
                      prefix={<SearchOutlined />}
                      placeholder="搜索部门名"
                      value={deptKeyword}
                      onChange={e => { setDeptKeyword(e.target.value); loadDeptTree(e.target.value); }}
                      className="mb-2"
                      allowClear
                    />
                    {deptLoading ? (
                      <div className="flex justify-center mt-8"><Spin /></div>
                    ) : (
                      <Tree
                        checkable
                        showIcon
                        treeData={deptTreeData}
                        checkedKeys={checkedDeptKeys}
                        onCheck={keys => handleDeptCheck(keys as React.Key[])}
                        style={{ maxHeight: 300, overflowY: 'auto' }}
                      />
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>

        {/* 右栏：已选列表 */}
        <div
          className="border rounded flex flex-col"
          style={{ width: 220, flexShrink: 0 }}
        >
          <div className="px-3 py-2 border-b text-xs text-gray-500 font-medium">
            已选&nbsp;<span className="text-blue-500">{selected.length}</span>&nbsp;项
          </div>
          <div className="flex-1 overflow-y-auto p-1">
            {selected.length === 0 ? (
              <div className="flex items-center justify-center h-full text-xs text-gray-400 py-6">
                暂未选择
              </div>
            ) : (
              <div className="space-y-0.5">
                {selected.map(item => (
                  <div
                    key={`${item.granteeType}-${item.granteeId}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 group"
                  >
                    <div className="shrink-0">
                      {item.granteeType === 'USER' ? (
                        <Avatar size={22} icon={<UserOutlined />} src={item.avatarUrl ?? undefined} style={{ background: '#1677ff' }} />
                      ) : (
                        <Avatar size={22} icon={<ApartmentOutlined />} style={{ background: '#52c41a' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs truncate leading-tight">{item.displayName}</div>
                      <div className="text-[10px] text-gray-400 leading-tight">
                        {item.granteeType === 'USER' ? '人员' : '部门'}
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      className="shrink-0 !w-5 !h-5 !min-w-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      icon={<CloseOutlined style={{ fontSize: 10 }} />}
                      onClick={() => handleRemove(item.granteeType, item.granteeId)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GranteePickerModal;
